import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router';
import { compactDecrypt } from 'jose';
import { RelayEnvironmentProvider } from 'react-relay';
import { z } from 'zod/mini';
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog';
import { Footer } from '../layout/Footer';
import { Header } from '../layout/Header';
import { relayEnv } from '../relay/relay-env';
import { spSiteContext } from '../sp-site-context/sp-site-context';
import { getUser, setUser } from '../store/userStore';
import { userSessionStorageKey } from '../store/userSessionStorageKey';

export const Route = createRootRouteWithContext<IRouterContext>()({
    beforeLoad: async (ctx) => {
        // Beim Application-Start (erster Eintritt ins Root-Layout)
        // den User aus sessionStorage wiederherstellen.
        // Der launchCode-Flow läuft bereits in main.tsx ab und hat
        // sessionStorage zu diesem Zeitpunkt bereits befüllt.

        if (ctx.cause === 'enter') {
            const encUser = sessionStorage.getItem(userSessionStorageKey);
            let user: IUser | undefined;

            if (encUser) {
                try {
                    const decResult = await compactDecrypt(
                        encUser,
                        new TextEncoder().encode(userSessionStorageKey + location.hostname),
                        { keyManagementAlgorithms: ['PBES2-HS256+A128KW'] }
                    );
                    user = JSON.parse(new TextDecoder().decode(decResult.plaintext));
                } catch {
                    // fehlerhafter/abgelaufener Launch-Context → ignorieren
                }
            }

            setUser(user);
        }

        // aktuellen User aus dem Store lesen (auch bei Re-Runs ohne cause='enter')
        const user = getUser();
        let site;
        if (user) {
            site = await spSiteContext.get(user.siteUrl);
        }

        if (import.meta.env.LOGIN_ENABLED) {
            if (ctx.location.pathname === '/login') {
                return { site, user };
            } else if (!user || !site) {
                // @ts-ignore
                throw redirect({ to: '/login' });
            }
        }

        // Kein User/Site vorhanden → nach /no-site leiten
        if ((!user || !site) && ctx.location.pathname !== '/no-site') {
            throw redirect({ to: '/no-site' });
        }

        // Launch-Context an Sub-Routes weiterreichen
        return { site, user };
    },

    component: () => {
        return (
            <RelayEnvironmentProvider environment={relayEnv}>
                <Header />
                <Outlet />
                <Footer />
                <ConfirmationDialog />
            </RelayEnvironmentProvider>
        );
    },
    validateSearch: z.object({
        launchCode: z.optional(z.string()),
    }),
});
